import AddIcon from "@mui/icons-material/Add";
import { InputAdornment, MenuItem, TextField } from "@mui/material";
import { Dispatch, Fragment, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { EntriesGroupAndSortKey, MAX_GROUPS, MIN_GROUPS } from "../constants";
import { GroupKey } from "../types";

type Props = {
  selectedGroups: GroupKey[];
  setSelectedGroups: Dispatch<SetStateAction<GroupKey[]>>;
};

const GROUPING_OPTIONS = Object.values(EntriesGroupAndSortKey);

const GroupFields = ({ selectedGroups, setSelectedGroups }: Props) => {
  const { t } = useTranslation("hourEntries");

  const getStartAdornment = (index: number) =>
    selectedGroups[index]
      ? { startAdornment: null }
      : {
          startAdornment: (
            <InputAdornment position="start">
              <AddIcon />
            </InputAdornment>
          ),
        };

  const handleGroupChange = (newGroup: GroupKey, index: number) => {
    if (!newGroup) {
      return setSelectedGroups((prevGroups) =>
        prevGroups.filter((_, ind) => ind !== index),
      );
    }

    const newGroups = [...selectedGroups];
    const groupIndex = newGroups.indexOf(newGroup);

    const updateExistingGroups = () => {
      newGroups.splice(groupIndex, 1);
      newGroups.splice(index, 0, newGroup);
      return setSelectedGroups(newGroups);
    };

    const existingGroup = groupIndex !== -1;
    const updateGroup = index < newGroups.length;

    if (existingGroup || updateGroup) {
      return updateExistingGroups();
    }

    return setSelectedGroups((prevGroups) => [...prevGroups, newGroup]);
  };

  const renderGroupField = (index: number) => {
    const clearDisabled = selectedGroups?.length === MIN_GROUPS;
    return (
      <Fragment key={selectedGroups[index]}>
        <TextField
          sx={{ marginBottom: "8px" }}
          key={index}
          value={selectedGroups[index]}
          select
          fullWidth
          size="small"
          SelectProps={{ displayEmpty: true }}
          onChange={(e) => handleGroupChange(e.target.value as GroupKey, index)}
        >
          {selectedGroups[index] && (
            <MenuItem value="" disabled={clearDisabled}>
              {t("common:clear")}
            </MenuItem>
          )}
          {GROUPING_OPTIONS.map((group) => (
            <MenuItem key={group} value={group}>
              {t(group)}
            </MenuItem>
          ))}
        </TextField>
        {selectedGroups.length < MAX_GROUPS &&
          index === selectedGroups.length - 1 && (
            <TextField
              select
              fullWidth
              size="small"
              value={""}
              SelectProps={{ displayEmpty: true }}
              onChange={(e) =>
                handleGroupChange(
                  e.target.value as GroupKey,
                  selectedGroups.length,
                )
              }
              InputProps={{
                ...getStartAdornment(selectedGroups.length),
              }}
            >
              <MenuItem sx={{ display: "none" }} disabled value="">
                {t("select...")}
              </MenuItem>
              {GROUPING_OPTIONS.map((group) => (
                <MenuItem
                  disabled={selectedGroups.includes(group as GroupKey)}
                  key={group}
                  value={group}
                >
                  {t(group)}
                </MenuItem>
              ))}
            </TextField>
          )}
      </Fragment>
    );
  };

  return (
    <>
      {!selectedGroups.length ? (
        <TextField
          select
          fullWidth
          size="small"
          value={""}
          SelectProps={{ displayEmpty: true }}
          onChange={(e) =>
            handleGroupChange(e.target.value as GroupKey, selectedGroups.length)
          }
          InputProps={{
            ...getStartAdornment(selectedGroups.length),
          }}
        >
          <MenuItem sx={{ display: "none" }} disabled value="">
            {t("hourEntries:select...")}
          </MenuItem>
          {GROUPING_OPTIONS.map((group) => (
            <MenuItem key={group} value={group}>
              {t(group)}
            </MenuItem>
          ))}
        </TextField>
      ) : (
        selectedGroups.map((_, index) => renderGroupField(index))
      )}
    </>
  );
};

export default GroupFields;
